import { path as d3path } from "d3";
import { SankeyLink, SankeyNode, sankey } from "d3-sankey";
import { useContext, useRef, useState } from "react";

import { withErrorBoundary } from "@client/components/utils/ErrorBoundary";
import { formatMoney } from "@client/utils/formatMoney";
import { NodeColors } from "@client/utils/NodeColors";
import { sankeyPath } from "@client/utils/tooltips";
import { useOnResize } from "@client/utils/useOnResize";
import { Node } from "@engine/round/aggregate";
import { SProcessOutput } from "@lib/algoInterface/runSProcessOnRound";
import { generateProposalId } from "@lib/utils/idGeneration";
import { orderBy } from "lodash";
import {
  PinnedNodesContext,
  StateContext,
  useEvaluableControls,
} from "../../../../pages/round/Round";

// how many layers of nodes will be shown. Used for sizing the svg to save room for rightmost text
// The number of layers is controlled by sankey, however it is difficult to retriev that information,
// and for our usecase it will always be 3
const numLayers = 3;

const amountWidth = 60;
const fontSize = 12;
const descenderSize = 3;
const padding = 5;

type ChartNodeBody = {
  evaluable: Node;
  layer: number;
  nodeIndex: number | null;
};

// The types for sankey are not quite correct, here we have a hacky workaround to fix them for our
// relatively narrow usecase
type ChartNode = SankeyNode<ChartNodeBody, object> & {
  source?: SankeyNode<ChartNodeBody, object>;
  target?: SankeyNode<ChartNodeBody, object>;
};
type ChartLink = SankeyLink<ChartNodeBody, object>;

type SankeyData = {
  nodes: ChartNode[];
  links: ChartLink[];
};

export const AlluvialChart = withErrorBoundary(
  ({ results }: { results: SProcessOutput }) => {
    const { hoveredNode, setHoveredNode } = useContext(PinnedNodesContext);

    const [dimensions, setDimensions] = useState({
      height: 400,
      width: 600,
    });
    const ref = useRef<HTMLDivElement>(null);

    useOnResize(() => {
      if (ref.current) {
        if (
          ref.current.clientHeight === dimensions.height &&
          ref.current.clientWidth === dimensions.width
        ) {
          return;
        }

        setDimensions({
          height: ref.current.clientHeight,
          width: ref.current.clientWidth,
        });
      }
    });

    const sankeyData = getNodesAndLinks(results);

    const { nodes, links } = sankeySetup(
      sankeyData,
      dimensions.height,
      dimensions.width,
    );

    const funderBudget =
      (
        nodes.find((node) => node.evaluable.type === "funder")?.evaluable as
          | { budget: number }
          | undefined
      )?.budget || 0;

    if (funderBudget === 0) {
      return (
        <div className="pt-4 w-[500px] mx-auto">
          The sankey diagram is hidden because the funder budget is currently
          set to zero. The funder budget can be changed by going to the JT view
          and on the Incremental Evals tab making an allocation to a
          recommender.
        </div>
      );
    }

    return (
      <div
        className="flex-1 mb-4 mx-[75px] select-none overflow-hidden"
        ref={ref}
      >
        <svg width={dimensions.width} height={dimensions.height}>
          <g>
            {links.map((link) => (
              <LinkDrawing
                key={link.index}
                link={link}
                hoveredNode={hoveredNode}
                setHoveredNode={setHoveredNode}
              />
            ))}
            {
              // Sort nodes by y-position for z-index ordering purposes. Labels
              // on org nodes have an opaque descender, so we want nodes below it
              // to be drawn after.
              orderBy(nodes, (node) => node.y0).map((node) => (
                <NodeDrawing
                  key={node.index}
                  node={node}
                  svgWidth={dimensions.width}
                  hoveredNode={hoveredNode}
                  setHoveredNode={setHoveredNode}
                />
              ))
            }
          </g>
        </svg>
      </div>
    );
  },
);

function getNodesAndLinks(results: SProcessOutput): SankeyData {
  const holdProposalId = results.holdProposalId;
  const links: ChartLink[] = [];
  const nodes: ChartNode[] = [];

  for (let i = 0; i < results.parentNodes.length; i++) {
    const funder = results.parentNodes[i];
    nodes.push({
      evaluable: funder,
      layer: 0,
      nodeIndex: i,
    });
  }

  const layerHoldNodes: Node[] = [];

  for (let i = 0; i < results.layerNodes.length; i++) {
    for (let j = 0; j < results.layerNodes[i].length; j++) {
      let node = results.layerNodes[i][j];

      // if the node has no value flowing to it, don't show it
      if (
        results.layerTargetTotals[i].get(j) === 0 &&
        node.id !== holdProposalId
      ) {
        continue;
      }

      // only use the real hold proposal in the last layer
      if (node.id === holdProposalId) {
        if (i !== results.layerNodes.length - 1) {
          node = {
            id: generateProposalId(),
            shortname: "Hold",
            type: "recommender",
            status: "active",
            evaluations: {},
            memberId: "",
            trancheList: [],
            outsideDollarSubstitutionRate: 0,
          };
        }
        layerHoldNodes.push(node);
      }

      const isLastLayer = i === results.layerNodes.length - 1;

      let nodeIndex: number | null;
      if (!isLastLayer) {
        if (node.shortname === "Hold") {
          nodeIndex = null;
        } else {
          nodeIndex = j - 1;
        }
      } else {
        nodeIndex = j;
      }

      nodes.push({
        evaluable: node,
        layer: i + 1,
        nodeIndex: nodeIndex,
      });
    }
  }

  let previousLayerNodes = results.parentNodes;
  for (let i = 0; i < results.layerAccounts.length; i++) {
    const previousLayerHadHold = previousLayerNodes[0].id === holdProposalId;
    const skipHoldAsSource = previousLayerHadHold ? 1 : 0;

    if (previousLayerHadHold) {
      const holdValue = results.layerAccounts[i - 1].get(0, 0);

      if (holdValue > 0) {
        links.push({
          source: layerHoldNodes[i - 1].id,
          target: layerHoldNodes[i].id,
          value: holdValue,
        });
      }
    }

    for (let k = skipHoldAsSource; k < previousLayerNodes.length; k++) {
      for (let j = 0; j < results.layerNodes[i].length; j++) {
        // the layer will have one column less than the previous layer if the previous layer had hold
        // (node is not an evaluator, columns in accountables correspond to evaluators)
        const value = results.layerAccounts[i].get(k - skipHoldAsSource, j);
        if (value === 0) {
          continue;
        }

        let target = results.layerNodes[i][j];
        const source = previousLayerNodes[k];

        if (target.id === holdProposalId) {
          target = layerHoldNodes[i];
        }

        links.push({
          source: source.id,
          target: target.id,
          value,
        });
      }
    }

    previousLayerNodes = results.layerNodes[i];
  }

  return { nodes, links };
}

type ChartLinkWithX = ChartLink & {
  x0: number;
  x1: number;
};

function sankeySetup(
  data: SankeyData,
  height: number,
  width: number,
): { nodes: Required<ChartNode>[]; links: Required<ChartLinkWithX>[] } {
  return sankey<ChartNode, ChartLink>()
    .nodeId((d) => d.evaluable.id)
    .nodeWidth(18)
    .nodePadding(10)
    .extent([
      [1, 10],
      [(width / (numLayers + 1)) * numLayers, height - 5],
    ])(data) as unknown as {
    nodes: Required<ChartNode>[];
    links: Required<ChartLinkWithX>[];
  };
}

function drawNodeText(
  node: Required<ChartNode>,
  value: number,
  isFocus: boolean,
) {
  const fontWeight = isFocus ? "bold" : "normal";

  return (
    <>
      <text
        fontFamily="sans-serif"
        fontSize={fontSize}
        className="fill-black dark:fill-white"
        x={node.x1 + padding}
        y={(node.y1 + node.y0) / 2}
        dy="0.35em"
        textAnchor="start"
        fontWeight={fontWeight}
      >
        {`${node.evaluable.shortname}, ${formatMoney(value)}`}
      </text>
    </>
  );
}

// TODO: should probably be a react component
function drawProposalNodeText(
  node: Required<ChartNode>,
  value: number,
  isFocus: boolean,
  svgWidth: number,
) {
  const fontWeight = isFocus ? "bold" : "normal";
  const nodeMiddle = (node.y1 + node.y0) / 2;
  const columnWidth = svgWidth / (numLayers + 1);

  return (
    <>
      <text
        fontFamily={"sans-serif"}
        fontSize={fontSize}
        className="fill-black dark:fill-white"
        x={node.x1 + padding}
        y={nodeMiddle}
        dy={"0.35em"}
        textAnchor={"start"}
        fontWeight={fontWeight}
      >
        {`${node.evaluable.shortname}`}
      </text>
      <rect
        x={node.x1 + columnWidth - amountWidth}
        y={nodeMiddle - fontSize / 2}
        height={fontSize + descenderSize}
        width={
          /* +1 width to deal with an integer-rounding issue in coordinates,
           * which would cause a one-pixel-wide column of stray overflow text
           * to appear on the right edge
           */
          amountWidth + 1
        }
        className="fill-[#f5f5f4] dark:fill-[#181818]"
      />
      <text
        enableBackground="true"
        fontFamily={"sans-serif"}
        fontSize={fontSize}
        className="fill-black dark:fill-white"
        x={node.x1 + columnWidth - amountWidth + padding}
        y={nodeMiddle}
        dy={"0.35em"}
        textAnchor={"start"}
        fontWeight={fontWeight}
      >
        {`${formatMoney(value)}`}
      </text>
    </>
  );
}

function NodeDrawing({
  node,
  svgWidth,
  hoveredNode,
  setHoveredNode,
}: {
  node: Required<ChartNode>;
  svgWidth: number;
  hoveredNode: string | null;
  setHoveredNode: (evaluable: string | null) => void;
}) {
  const { round } = useContext(StateContext);
  const evaluableControls = useEvaluableControls(node.evaluable.id);
  const height = node.y1 - node.y0;
  const width = node.x1 - node.x0;
  const displayValue = node.value;

  // TODO: 0 value nodes should have been filtered out before we get here
  if (node.evaluable.shortname === "Hold" && displayValue === 0) {
    return;
  }

  return (
    <g key={node.evaluable.id}>
      <g
        onMouseEnter={() => setHoveredNode(node.evaluable.shortname)}
        onMouseLeave={() => setHoveredNode(null)}
      >
        <rect
          x={node.x0}
          y={node.y0}
          height={height}
          width={width}
          fill={NodeColors.getColor(round, node.evaluable)}
          opacity={evaluableControls.isFocus ? 0.95 : 0.8}
        />
        <title>{sankeyPath(node.evaluable.shortname)}</title>
        {node.evaluable.type === "proposal"
          ? drawProposalNodeText(
              node,
              displayValue,
              hoveredNode === node.evaluable.shortname,
              svgWidth,
            )
          : drawNodeText(
              node,
              displayValue,
              hoveredNode === node.evaluable.shortname,
            )}
      </g>
    </g>
  );
}

function LinkDrawing({
  link,
  hoveredNode,
  setHoveredNode,
}: {
  link: Required<ChartLink>;
  hoveredNode: string | null;
  setHoveredNode: (evaluable: string | null) => void;
}) {
  const { round } = useContext(StateContext);
  const source = link.source as Required<ChartNode>;
  const target = link.target as Required<ChartNode>;

  const relatedShortNames = source.targetLinks.map((targetLink) => {
    const source = targetLink.source as Required<ChartNode>;
    return source.evaluable.shortname;
  });

  relatedShortNames.push(
    ...source.sourceLinks.map((sourceLink) => {
      const target = sourceLink.target as Required<ChartNode>;
      return target.evaluable.shortname;
    }),
  );

  // Source: https://stackoverflow.com/questions/72584917/how-to-fix-unwanted-circle-on-break-down-of-svg-path-element-for-sankey-links/72593081#72593081
  function sankeyLinkPath(link) {
    /**
     * This function is a drop in replacement for d3.sankeyLinkHorizontal().
     * Except any accessors/options.
     */
    // Start and end of the link
    let sx1 = link.source.x1;
    let tx0 = link.target.x0 + 1;

    // All four outer corners of the link
    // where e.g. lsy0 is the upper corner of the link on the source side
    let lsy0 = link.y0 - link.width / 2;
    let lsy1 = link.y0 + link.width / 2;
    let lty0 = link.y1 - link.width / 2;
    let lty1 = link.y1 + link.width / 2;

    // Center (x) of the link
    let lcx = sx1 + (tx0 - sx1) / 2;

    // Define outline of link as path
    let path = d3path();
    path.moveTo(sx1, lsy0);
    path.bezierCurveTo(lcx, lsy0, lcx, lty0, tx0, lty0);
    path.lineTo(tx0, lty1);
    path.bezierCurveTo(lcx, lty1, lcx, lsy1, sx1, lsy1);
    path.lineTo(sx1, lsy0);
    return path.toString();
  }
  const pathData = sankeyLinkPath(link);

  return (
    <g
      key={`${source.evaluable.id}-${target.evaluable.id}`}
      className="mix-blend-normal dark:mix-blend-lighten"
    >
      <path
        d={pathData}
        fill={NodeColors.getColor(round, source.evaluable)}
        className={
          hoveredNode === source.evaluable.shortname
            ? "opacity-60 dark:opacity-75"
            : "opacity-40"
        }
        onMouseEnter={() => setHoveredNode(source.evaluable.shortname)}
        onMouseLeave={() => setHoveredNode(null)}
      >
        <title>{sankeyPath(source.evaluable.shortname)}</title>
      </path>
    </g>
  );
}
